import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AllReportsModal } from "./AllReportsModal";
import { AllOccurrencesModal } from "./AllOccurrencesModal";
import { WorkforceModal } from "./WorkforceModal";
import { EquipmentModal } from "./EquipmentModal";
import { PhotoGalleryModal } from "./PhotoGalleryModal";
import { AttachmentsModal } from "./AttachmentsModal";
import {
  FileText,
  AlertTriangle,
  Users,
  Truck,
  Camera,
  Paperclip,
  CheckSquare,
  X
} from "lucide-react";
import { ApproveRDOs } from "./ApproveRDOs";
import { WorkApproveRDOs } from "../work-panel/WorkApproveRDOs";
import { getObraStats, getGlobalStats, type ObraStats } from "@/services/obraStats";

interface StatsCardsProps {
  visibleSubItems?: {
    reports?: boolean;
    occurrences?: boolean;
    workforce?: boolean;
    equipment?: boolean;
    photos?: boolean;
    attachments?: boolean;
    approveRdos?: boolean;
  };
  onCloseCard?: (cardId: string) => void;
  // Work panel specific props
  workContext?: {
    obraId: string;
    obraName: string;
  };
}

export function StatsCards({ visibleSubItems = {}, onCloseCard, workContext }: StatsCardsProps) {
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [isOccurrencesModalOpen, setIsOccurrencesModalOpen] = useState(false);
  const [isWorkforceModalOpen, setIsWorkforceModalOpen] = useState(false);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);
  const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
  const [isApproveRdosModalOpen, setIsApproveRdosModalOpen] = useState(false);

  // Números reais derivados dos RDOs (da obra, ou globais fora do painel)
  const [obraStats, setObraStats] = useState<ObraStats | null>(null);
  useEffect(() => {
    let active = true;
    (workContext ? getObraStats(workContext.obraId) : getGlobalStats())
      .then((s) => { if (active) setObraStats(s); })
      .catch((e) => console.error("StatsCards stats:", e));
    return () => { active = false; };
  }, [workContext?.obraId]);

  const stats = [
    {
      title: "Relatórios Preenchidos",
      value: String(obraStats?.totalRdos ?? "—"),
      icon: FileText,
      color: "text-primary",
      bgColor: "bg-primary-light",
    },
    {
      title: "Ocorrências",
      value: String(obraStats?.totalOcorrencias ?? "—"),
      icon: AlertTriangle,
      color: "text-warning",
      bgColor: "bg-warning-light",
    },
    {
      title: "Mão de Obra",
      value: String(obraStats?.maoDeObraUltimo ?? "—"),
      icon: Users,
      color: "text-success",
      bgColor: "bg-success-light",
    },
    {
      title: "Equipamentos",
      value: String(obraStats?.equipamentosUltimo ?? "—"),
      icon: Truck,
      color: "text-primary",
      bgColor: "bg-primary-light",
    },
    {
      title: "Fotos",
      value: String(obraStats?.totalFotos ?? "—"),
      icon: Camera,
      color: "text-success",
      bgColor: "bg-success-light",
    },
    {
      title: "Anexos",
      value: String(obraStats?.totalAnexos ?? "—"),
      icon: Paperclip,
      color: "text-muted-foreground",
      bgColor: "bg-muted",
    },
    {
      title: "Aprovar RDOs",
      value: String(obraStats?.pendentesAprovacao ?? "—"),
      icon: CheckSquare,
      color: "text-warning",
      bgColor: "bg-warning-light",
    },
  ];

  // Filter stats based on visibility
  const visibleStats = stats.filter(stat => {
    switch (stat.title) {
      case "Relatórios Preenchidos":
        return visibleSubItems.reports !== false;
      case "Ocorrências":
        return visibleSubItems.occurrences !== false;
      case "Mão de Obra":
        return visibleSubItems.workforce !== false;
      case "Equipamentos":
        return visibleSubItems.equipment !== false;
      case "Fotos":
        return visibleSubItems.photos !== false;
      case "Anexos":
        return visibleSubItems.attachments !== false;
      case "Aprovar RDOs":
        // Don't render the card from main loop when in work context
        // WorkApproveRDOs component renders its own card
        return !workContext && visibleSubItems.approveRdos !== false;
      default:
        return true;
    }
  });

  const getCardId = (title: string) => {
    switch (title) {
      case "Relatórios Preenchidos":
        return "statsCards.reports";
      case "Ocorrências":
        return "statsCards.occurrences";
      case "Mão de Obra":
        return "statsCards.workforce";
      case "Equipamentos":
        return "statsCards.equipment";
      case "Fotos":
        return "statsCards.photos";
      case "Anexos":
        return "statsCards.attachments";
      case "Aprovar RDOs":
        return "statsCards.approveRdos";
      default:
        return "";
    }
  };

  const handleClose = (e: React.MouseEvent, title: string) => {
    e.stopPropagation();
    const cardId = getCardId(title);
    if (cardId && onCloseCard) {
      onCloseCard(cardId);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
      {visibleStats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Card 
            key={index} 
            className="shadow-card hover:shadow-card-hover transition-shadow cursor-pointer relative group"
            onClick={() => {
              if (stat.title === "Relatórios Preenchidos") {
                setIsReportsModalOpen(true);
              } else if (stat.title === "Ocorrências") {
                setIsOccurrencesModalOpen(true);
              } else if (stat.title === "Mão de Obra") {
                setIsWorkforceModalOpen(true);
              } else if (stat.title === "Equipamentos") {
                setIsEquipmentModalOpen(true);
              } else if (stat.title === "Fotos") {
                setIsPhotosModalOpen(true);
              } else if (stat.title === "Anexos") {
                setIsAttachmentsModalOpen(true);
              } else if (stat.title === "Aprovar RDOs") {
                setIsApproveRdosModalOpen(true);
              } else {
                console.log('Clicou no card:', stat.title);
              }
            }}
          >
            {/* Close button */}
            <button
              onClick={(e) => handleClose(e, stat.title)}
              className="absolute top-1 right-1 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100 sm:top-2 sm:right-2 sm:h-8 sm:w-8"
              aria-label={`Fechar ${stat.title}`}
            >
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
            
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
                {stat.title === "Mão de Obra" && obraStats && obraStats.totalRdos > 0 && (
                  <div className="text-xs font-normal text-muted-foreground/70 mt-0.5">
                    Média últ. 10 RDOs: {obraStats.mediaMaoDeObra10}
                  </div>
                )}
              </CardTitle>
              <div className={`p-2 rounded-lg ${stat.bgColor} flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{stat.value}</div>
            </CardContent>
          </Card>
        );
      })}
      
      <AllReportsModal
        open={isReportsModalOpen}
        onOpenChange={setIsReportsModalOpen}
        obraId={workContext?.obraId}
      />

      <AllOccurrencesModal
        open={isOccurrencesModalOpen}
        onOpenChange={setIsOccurrencesModalOpen}
        obraId={workContext?.obraId}
      />

      <WorkforceModal
        open={isWorkforceModalOpen}
        onOpenChange={setIsWorkforceModalOpen}
        obraId={workContext?.obraId}
      />

      <EquipmentModal
        open={isEquipmentModalOpen}
        onOpenChange={setIsEquipmentModalOpen}
        obraId={workContext?.obraId}
      />

      <PhotoGalleryModal
        open={isPhotosModalOpen}
        onOpenChange={setIsPhotosModalOpen}
        obraId={workContext?.obraId}
      />

      <AttachmentsModal
        open={isAttachmentsModalOpen}
        onOpenChange={setIsAttachmentsModalOpen}
        obraId={workContext?.obraId}
      />

      {workContext ? (
        <WorkApproveRDOs 
          isVisible={visibleSubItems.approveRdos !== false}
          onClose={() => onCloseCard?.('statsCards.approveRdos')}
          obraId={workContext.obraId}
          obraName={workContext.obraName}
        />
      ) : (
        <ApproveRDOs
          isVisible={isApproveRdosModalOpen}
          onClose={() => setIsApproveRdosModalOpen(false)}
        />
      )}
    </div>
  );
}